import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
// import { Link } from "react-router-dom";
// import QuestionBox2Rows from "../../Components/QuestionBox/QuestionBox2Rows";
import QuestionBox from "../../Components/QuestionBox/QuestionBox";

const normalizeId = (id) => {
    if (!id) return "";
    return typeof id === "object" && id.toString ? id.toString() : String(id);
};

// Function to extract YouTube video ID
const getYouTubeVideoId = (url) => {
    if (!url) return null;
    
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return match && match[2].length === 11 ? match[2] : null;
};

const IndividualNews = () => {
    const { newsId, newsSlug } = useParams();
    const navigate = useNavigate();

    const [newsData, setNewsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const videoRef = useRef(null);

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const response = await fetch("https://app.akiya2.com/all_news");
                const data = await response.json();
                setNewsData(data);

                const normalizedSearchId = normalizeId(newsId);
                const currentItem = data.find((item) => normalizeId(item._id) === normalizedSearchId);

                if (!currentItem) {
                    setNotFound(true);
                    return;
                }

                const generatedSlug = currentItem.news_heading
                    .toLowerCase()
                    .replace(/\s+/g, "-")
                    .replace(/[^\w-]+/g, "");

                if (newsSlug !== generatedSlug) {
                    navigate(`/news/${normalizedSearchId}/${generatedSlug}`, { replace: true });
                }

                setLoading(false);
            } catch (error) {
                console.error("Error fetching news:", error);
                setNotFound(true);
                setLoading(false);
            }
        };

        fetchNews();
    }, [newsId, newsSlug, navigate]);

    const toggleMute = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
        }
        
        setIsMuted(!isMuted);
    };

    // Function to render media based on type
    const renderMedia = (article) => {
        if (!article.coverImage) return null;

        // Check for media type
        if (article.media_type === "youtube") {
            const youtubeVideoId = getYouTubeVideoId(article.coverImage);
            if (youtubeVideoId) {
                return (
                    <div className="mb-6 relative">
                        <iframe 
                            ref={videoRef}
                            src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&mute=${isMuted ? 1 : 0}&controls=0&showinfo=0`}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            className="w-full h-[480px] rounded-xl"
                        ></iframe>
                        <button 
                            className="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded-full z-10"
                            onClick={toggleMute}
                        >
                            {isMuted ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M10 1L6 5H2v6h4l4 4V1z"></path>
                                    <line x1="17" y1="3" x2="8" y2="12"></line>
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M10 1L6 5H2v6h4l4 4V1z"></path>
                                    <path d="M14 5a4 4 0 0 1 0 6"></path>
                                    <path d="M17 3a7 7 0 0 1 0 10"></path>
                                </svg>
                            )}
                        </button>
                    </div>
                );
            }
        } else if (article.media_type === "video") {
            return (
                <div className="mb-6 relative">
                    <video 
                        ref={videoRef}
                        src={article.coverImage} 
                        className="w-full h-auto rounded-xl"
                        autoPlay
                        muted={isMuted}
                        loop
                        playsInline
                    >
                        Your browser does not support the video tag.
                    </video>
                    <button 
                        className="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded-full z-10"
                        onClick={toggleMute}
                    >
                        {isMuted ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M10 1L6 5H2v6h4l4 4V1z"></path>
                                <line x1="17" y1="3" x2="8" y2="12"></line>
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M10 1L6 5H2v6h4l4 4V1z"></path>
                                <path d="M14 5a4 4 0 0 1 0 6"></path>
                                <path d="M17 3a7 7 0 0 1 0 10"></path>
                            </svg>
                        )}
                    </button>
                </div>
            );
        } else {
            // Default to image
            return (
                <div className="mb-6">
                    <img
                        src={article.coverImage}
                        alt={article.news_heading}
                        className="w-full h-auto rounded-xl"
                        loading="eager"
                    />
                </div>
            );
        }
    };

    if (loading) return <div className="p-10 text-xl">Loading...</div>;
    if (notFound || !newsData.length) return <div className="p-10 text-xl text-red-600">News not found</div>;

    const normalizedSearchId = normalizeId(newsId);
    const article = newsData.find((item) => normalizeId(item._id) === normalizedSearchId);
    if (!article) return <div className="p-10 text-xl text-red-600">News not found</div>;

    return (
        <div className="bg-white">
            <div className="container mx-auto px-4 py-8">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Main content */}
                    <div className="lg:w-2/3">
                        {/* Replace static image with media renderer */}
                        {renderMedia(article)}

                        <div className="mb-6">
                            <div className="text-[#BF0013] font-medium mb-2 text-sm">{article.time}</div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-4">{article.news_heading}</h1>

                            {article.tags?.length > 0 && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-6">
                                    {article.tags.map((tag, idx) => (
                                        <span
                                            key={idx}
                                            className="bg-[#D0ECE7] text-gray-700 text-xs font-medium px-3 py-1 border border-gray-300 rounded-lg"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div
                            className="text-lg text-gray-800 leading-relaxed mb-8"
                            dangerouslySetInnerHTML={{ __html: article.news_content }}
                        ></div>

                        {(article.link || article.button_link) && (
                            <div className="mt-6">
                                <a
                                    href={article.button_link || article.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block bg-[--medium-sea-green] text-white px-5 py-3 rounded-lg hover:bg-[--sea-green] transition text-sm font-semibold"
                                >
                                    {article.button_name || "Read Full Article"}
                                </a>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="lg:w-1/3">
                        <div className="mb-8">
                            <h2 className="text-xl font-bold mb-4 text-[--sea-green]">More Features</h2>
                            <div className="space-y-4">
                                {newsData
                                    .filter((item) => normalizeId(item._id) !== normalizedSearchId)
                                    .reverse() // Reverse first to make latest items come first
                                    .slice(0, 6) // Then take the first 6 (which are now the latest)
                                    .map((item) => {
                                        const itemId = normalizeId(item._id);
                                        const itemSlug = item.news_heading
                                            .toLowerCase()
                                            .replace(/\s+/g, "-")
                                            .replace(/[^\w-]+/g, "");

                                        return (
                                            <a
                                                key={itemId}
                                                href={`/news/${itemId}/${itemSlug}`}
                                                className="group block p-3 mb-4 last:mb-0 bg-gray-100 rounded-xl hover:bg-[--sea-green] transition"
                                            >
                                                <div className="flex items-start space-x-3">
                                                    {/* Use company logo for video content, otherwise use the cover image */}
                                                    {item.coverImage && (
                                                        <img
                                                            src={(item.media_type === "youtube" || item.media_type === "video") 
                                                                ? "/images/ButtonLogo.webp" 
                                                                : item.coverImage}
                                                            alt={item.news_heading}
                                                            className="w-16 h-16 object-cover rounded group-hover:brightness-90 group-hover:scale-105 transition duration-200 my-Automatically "
                                                        />
                                                    )}
                                                    <div>
                                                        <h3 className="text-[18px] font-bold line-clamp-2 group-hover:text-white mb-1">
                                                            {item.news_heading}
                                                        </h3>
                                                        <p className="text-[14px] text-gray-600 group-hover:text-white">
                                                            {item.time}
                                                        </p>
                                                    </div>
                                                </div>
                                            </a>
                                        );
                                    })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="w-full flex justify-center items-center mb-[2rem]">
                <div className="w-full max-w-[1440px] ">
                    <QuestionBox />
                </div>
            </div>
        </div>
    );
};

export default IndividualNews;